import React, { useCallback, useContext } from 'react';
import get from 'lodash/get';
import { PapiDeviation } from '@wix/da-papi-types';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { useTranslation } from 'react-i18next';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import {
  ThumbFaveBiEvent,
  ThumbClickUnfaveBiEvent,
  BiData,
  BiContextProvidedValue,
  PopupClickAddToCollectionBiEvent,
} from '@wix/da-bi/pkg/events';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import ArrowDown from '@wix/da-ds/pkg/Icons/ArrowDown';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import { getDeviationResourceType } from '../../../../DeviationViews/_helpers';
import { NotificationType } from '../../../../Notifications';
import { getDeviationNotificationQueue } from '../../../../DeviationPage/redux/helpers';
import { useRestrictToLoggedIn } from '../../../../utils/hooks/useRestrictToLoggedIn';
import FavingIcon from '../../../../FaveButton/FavingIcon';
import ActionBarItemWrapper from '../../../_partials/items/ActionBarItemWrapper';
import ActionBarToggleButton from '../../../_partials/items/ActionBarToggleButton';
import SisuActionPopover from '../../../_partials/SisuActionPopover';
import { yieldToMain } from '@wix/da-hooks/pkg/yieldToMain';
import s from './FavouriteButton.scss';

export interface Props {
  deviation: PapiDeviation;
  actionBarType: 'primary' | 'secondary';
  onChangeFave: (deviation: PapiDeviation, to: boolean, notificationQueue: string) => void;
  onArrowClick?: () => void;
}

type FaveOrUnfaveBiEvent = ThumbFaveBiEvent | ThumbClickUnfaveBiEvent;

function getDeviationBiData(deviation: PapiDeviation) {
  return {
    creatorid: get(deviation, 'author.useridUuid'),
    typeid: getDeviationResourceType(deviation),
    itemid: deviation.deviationId,
  };
}

const FavouriteButton: React.FC<React.PropsWithChildren<Props>> = ({
  deviation,
  onChangeFave,
  onArrowClick,
}) => {
  const isMobile = useContext(MobileContext);
  const { t } = useTranslation();
  const isSisuPopoverEnabled = useFlag('sisu_popover');

  const { isLoggedIn, restrictToLoggedIn } = useRestrictToLoggedIn();

  const isFavourited = !!deviation.isFavourited;
  const queueName = isMobile ? 'default' : getDeviationNotificationQueue(deviation);
  const onClick = () => {
    return yieldToMain(() => onChangeFave(deviation, !isFavourited, queueName));
  };

  const FavingIconWithState = useCallback(
    iconProps => (
      <FavingIcon {...iconProps} isFavourited={isFavourited} deviationId={deviation.deviationId} />
    ),
    [isFavourited, deviation.deviationId]
  );

  const content = (
    <ActionBarItemWrapper
      notificationTypes={[
        NotificationType.Collect,
        NotificationType.CollectUndo,
        NotificationType.Fave,
        NotificationType.FaveUndo,
      ]}
      popoverWrapperClassName={s['button-with-dropdown']}
      renderReference={({ ref }) => (
        <>
          <ActionBarToggleButton
            onClick={!isLoggedIn && isSisuPopoverEnabled ? undefined : onClick}
            // Since mobile doesn't have a label, we'll duplicate the desktop one to cover it.
            aria-label={t(isFavourited ? 'actionbar.inFavourites' : 'actionbar.addToFavourites')}
            label={
              isMobile ? undefined : t(isFavourited ? 'actionbar.inFavourites' : 'actionbar.addToFavourites')
            }
            biData={BiData<FaveOrUnfaveBiEvent>({
              evid: isFavourited ? 109 : 102,
              creatorid: BiContextProvidedValue,
              typeid: BiContextProvidedValue,
              itemid: BiContextProvidedValue,
            })}
            icon={FavingIconWithState}
            iconRef={ref}
          />
          <PlainButtonOrLink
            className={s['arrow-down-wrapper']}
            onClick={
              !isLoggedIn && isSisuPopoverEnabled
                ? undefined
                : restrictToLoggedIn(ReferralType.FAVOURITE, onArrowClick)
            }
            biData={BiData<PopupClickAddToCollectionBiEvent>({
              evid: 333,
              sectionname: 'arrowButton',
            })}
            aria-label={t('actionbar.a11y.favouritePopupArrow')}
          >
            <ArrowDown size={IconSize.SMALLER} />
          </PlainButtonOrLink>
        </>
      )}
    />
  );

  return (
    <BiLoggerContextProvider value={getDeviationBiData(deviation)}>
      {!isLoggedIn && isSisuPopoverEnabled ? (
        <SisuActionPopover restrictedActionName={ReferralType.FAVOURITE}>{content}</SisuActionPopover>
      ) : (
        content
      )}
    </BiLoggerContextProvider>
  );
};
FavouriteButton.displayName = 'FavouriteButton';

export default FavouriteButton;
